export interface BoardFrise {
  id: string;
  familySlug: string;
  familyName: string;
  name: string;
  isDefault: boolean;
  steps: { position: number; label: string }[];
}

export function boardFrises(
  families: readonly {
    slug: string;
    name: string;
    frises: readonly { id: string; name: string; isDefault: boolean; steps: readonly string[] }[];
  }[],
): BoardFrise[] {
  return families.flatMap((family) =>
    family.frises.map((frise) => ({
      id: frise.id,
      familySlug: family.slug,
      familyName: family.name,
      name: frise.name,
      isDefault: frise.isDefault,
      steps: frise.steps.map((label, index) => ({ position: index + 1, label })),
    })),
  );
}

/** Dernière frise mémorisée, sinon la frise Contentieux par défaut. */
export function rememberedFriseId(
  frises: readonly BoardFrise[],
  stored: string | null,
): string | null {
  if (stored && frises.some((frise) => frise.id === stored)) return stored;
  return (
    frises.find((frise) => frise.familySlug === 'contentieux' && frise.isDefault)?.id
    ?? frises.find((frise) => frise.familySlug === 'contentieux')?.id
    ?? frises.find((frise) => frise.isDefault)?.id
    ?? frises[0]?.id
    ?? null
  );
}

export function columnsFor(
  frises: readonly BoardFrise[],
  friseId: string | null,
): BoardFrise['steps'] {
  if (!friseId) return [];
  return frises.find((frise) => frise.id === friseId)?.steps ?? [];
}

export function columnDossiers<T extends { friseTemplateId: string | null; position: number }>(
  dossiers: readonly T[],
  friseId: string,
): Map<number, T[]> {
  const placed = new Map<number, T[]>();
  for (const dossier of dossiers) {
    if (dossier.friseTemplateId !== friseId) continue;
    const list = placed.get(dossier.position) ?? [];
    list.push(dossier);
    placed.set(dossier.position, list);
  }
  return placed;
}

/** Pas de frise choisie : le filtre d'étape n'existe pas. */
export function stepFilterChoices(
  frises: readonly BoardFrise[],
  friseId: string | null,
): BoardFrise['steps'] {
  return columnsFor(frises, friseId);
}

export interface FamilyCountRow {
  familyName: string;
  friseId: string;
  friseName: string;
  position: number;
  label: string;
  title: string;
}

export interface FamilyStepLine {
  friseId: string;
  position: number;
  text: string;
  count: number;
  titles: string[];
}

export interface FamilyStepCount {
  familyName: string;
  lines: FamilyStepLine[];
}

/**
 * Compte par famille, une ligne par étape de chaque frise.
 * Deux frises de la même famille ne fusionnent pas une étape homonyme.
 */
export function familyStepCounts(rows: readonly FamilyCountRow[]): FamilyStepCount[] {
  const familyOrder: string[] = [];
  const byFamily = new Map<string, FamilyCountRow[]>();
  for (const row of rows) {
    if (!byFamily.has(row.familyName)) {
      familyOrder.push(row.familyName);
      byFamily.set(row.familyName, []);
    }
    byFamily.get(row.familyName)!.push(row);
  }

  return familyOrder.map((familyName) => {
    const items = byFamily.get(familyName)!;
    const qualify = new Set(items.map((item) => item.friseId)).size > 1;
    const lineOrder: string[] = [];
    const lines = new Map<string, FamilyStepLine>();
    for (const item of items) {
      const key = `${item.friseId}:${item.position}`;
      let line = lines.get(key);
      if (!line) {
        line = {
          friseId: item.friseId,
          position: item.position,
          text: qualify ? `${item.friseName} — ${item.label}` : item.label,
          count: 0,
          titles: [],
        };
        lines.set(key, line);
        lineOrder.push(key);
      }
      line.count += 1;
      if (line.titles.length < 2) line.titles.push(item.title);
    }
    return {
      familyName,
      lines: lineOrder
        .map((key) => lines.get(key)!)
        .sort((a, b) => a.position - b.position || a.text.localeCompare(b.text, 'fr')),
    };
  });
}

export function currentStepLabel(
  steps: readonly { position: number; label: string }[] | undefined,
  position: number,
): string | null {
  return steps?.find((step) => step.position === position)?.label ?? null;
}

export function dossierBoardRef(row: object): { friseTemplateId: string | null; position: number } {
  const record = row as { frise_template_id?: string | null; current_frise_position?: number | null };
  return {
    friseTemplateId: record.frise_template_id ?? null,
    position: record.current_frise_position ?? 1,
  };
}
